import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

interface Region {
  id: number;
  name_ar: string;
}

interface City {
  id: number;
  name_ar: string;
}

const RegisterPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [accountType, setAccountType] = useState('individual');
  const [companyName, setCompanyName] = useState('');
  const [regionId, setRegionId] = useState('');
  const [cityId, setCityId] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [regions, setRegions] = useState<Region[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    api.get('/regions').then((res) => setRegions(res.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (regionId) {
      api.get(`/regions/${regionId}/cities`).then((res) => setCities(res.data.data || [])).catch(() => setCities([]));
    } else {
      setCities([]);
      setCityId('');
    }
  }, [regionId]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !phone || !password) {
      toast.error('أكمل جميع البيانات المطلوبة');
      return;
    }
    if (password !== passwordConfirm) {
      toast.error('كلمة المرور غير متطابقة');
      return;
    }
    if (!cityId) {
      toast.error('اختر المدينة');
      return;
    }
    if (!termsAccepted) {
      toast.error('يجب الموافقة على الشروط والأحكام');
      return;
    }
    if (accountType === 'company' && !companyName) {
      toast.error('أدخل اسم الشركة أو المؤسسة');
      return;
    }
    try {
      setLoading(true);
      const formattedPhone = phone.startsWith('0') ? phone : `0${phone}`;
      const payload: any = {
        name,
        email,
        phone: formattedPhone,
        password,
        password_confirmation: passwordConfirm,
        account_type: accountType,
        city_id: parseInt(cityId),
        terms_accepted: true,
      };
      if (accountType === 'company') {
        payload.company_name = companyName;
      }
      const res = await api.post('/auth/register', payload);
      const { token, user } = res.data.data;
      login(token, user);
      toast.success(`أهلاً ${user.name}! تم إنشاء حسابك بنجاح`);
      navigate('/');
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (errors) {
        const firstError = Object.values(errors)[0];
        toast.error(Array.isArray(firstError) ? firstError[0] as string : 'حدث خطأ');
      } else {
        toast.error(err.response?.data?.message || 'حدث خطأ');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-8">
      <div className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-primary-500 mb-2">الجلب</h1>
          <p className="text-gray-500 text-sm">إنشاء حساب جديد</p>
        </div>

        <form onSubmit={handleRegister}>
          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">الاسم *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="اسمك الكامل"
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">البريد الإلكتروني *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@email.com"
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              dir="ltr"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">رقم الجوال *</label>
            <div className="flex gap-2" dir="ltr">
              <span className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-3 text-sm text-gray-500">+966</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                placeholder="5XXXXXXXX"
                className="flex-1 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                maxLength={10}
                dir="ltr"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">كلمة المرور *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="كلمة المرور"
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">تأكيد كلمة المرور *</label>
            <input
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="أعد كتابة كلمة المرور"
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">نوع الحساب</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setAccountType('individual')}
                className={`flex-1 py-3 rounded-lg text-sm font-semibold border-2 transition ${accountType === 'individual' ? 'border-primary-500 bg-primary-50 text-primary-500' : 'border-gray-200 text-gray-500'}`}
              >
                فرد
              </button>
              <button
                type="button"
                onClick={() => setAccountType('company')}
                className={`flex-1 py-3 rounded-lg text-sm font-semibold border-2 transition ${accountType === 'company' ? 'border-primary-500 bg-primary-50 text-primary-500' : 'border-gray-200 text-gray-500'}`}
              >
                تاجر
              </button>
            </div>

            {accountType === 'company' && (
              <div className="mt-3">
                <label className="block text-sm text-gray-600 mb-2">اسم الشركة / المؤسسة *</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="اسم الشركة أو المؤسسة"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">المنطقة *</label>
            <select
              value={regionId}
              onChange={(e) => setRegionId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">اختر المنطقة</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>{r.name_ar}</option>
              ))}
            </select>
          </div>

          {cities.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">المدينة *</label>
              <select
                value={cityId}
                onChange={(e) => setCityId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">اختر المدينة</option>
                {cities.map((c) => (
                  <option key={c.id} value={c.id}>{c.name_ar}</option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-600">
                أوافق على{' '}
                <Link to="/terms" className="text-primary-500 underline">الشروط والأحكام</Link>
                {' '}و{' '}
                <Link to="/privacy" className="text-primary-500 underline">سياسة الخصوصية</Link>
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-500 text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition disabled:opacity-50"
          >
            {loading ? 'جاري التسجيل...' : 'إنشاء حساب'}
          </button>

          <p className="text-center text-sm text-gray-400 mt-6">
            لديك حساب؟{' '}
            <Link to="/login" className="text-primary-500 font-semibold">تسجيل الدخول</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
